import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Clock, AlertTriangle } from 'lucide-react';
import API_URL from '@/config/api';

const OPTIONS = [
  {
    value: 'urgent',
    color: '#dc2626',
    bg: '#fef2f2',
    border: '#fecaca',
    emoji: '🔴',
    label: 'Urgent — 1 to 3 Working Days',
    blurb: 'Use this if the materials are genuinely required within the next 1–3 working days. Your quote will be marked as Priority and moved to the urgent queue.',
  },
  {
    value: 'soon',
    color: '#ea580c',
    bg: '#fff7ed',
    border: '#fed7aa',
    emoji: '🟠',
    label: 'Soon — 4 Working Days to 1 Week',
    blurb: 'Use this if the materials are needed soon, but not immediately. Your quote will be processed through our standard queue.',
  },
  {
    value: 'no_rush',
    color: '#16a34a',
    bg: '#f0fdf4',
    border: '#bbf7d0',
    emoji: '🟢',
    label: 'No Immediate Rush',
    blurb: 'Use this if you are planning ahead, pricing a project, or do not have an immediate requirement. Your quote will be processed through our standard queue.',
  },
];

/**
 * Standalone priority picker — drop at the end of any order builder.
 *  - Defaults to `no_rush`
 *  - Disables the 'urgent' option if the user account doesn't have priority_quote_enabled
 *  - Calls onChange(value) when selection changes
 */
export default function PrioritySelector({ value = 'no_rush', onChange, className = '' }) {
  const [priorityEnabled, setPriorityEnabled] = useState(true);
  useEffect(() => {
    fetch(`${API_URL}/api/auth/me`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(u => {
        if (u && typeof u.priority_quote_enabled === 'boolean') {
          setPriorityEnabled(u.priority_quote_enabled);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <Card className={`p-5 ${className}`} data-testid="priority-selector-card">
      <div className="flex items-center gap-2 mb-2">
        <Clock className="w-5 h-5 text-gray-700" />
        <h3 className="font-bold text-gray-900">When do you need these materials?</h3>
      </div>
      <p className="text-sm text-gray-600 mb-3">
        {priorityEnabled
          ? <>You currently have <strong className="text-rose-700">Priority Quote enabled</strong> on your account. If your order is urgent, you can mark this quote as <strong>Priority</strong> and it will be moved to our urgent queue. Priority quotes are normally reviewed within <strong>30 minutes</strong> during working hours.</>
          : <>Your account does not currently have Priority Quote enabled. Please contact Ox Resin if your order is urgent.</>
        }
      </p>
      <p className="text-sm text-gray-600 mb-4">Please select the option that best reflects when you require the materials:</p>

      <div className="space-y-2.5">
        {OPTIONS.map(opt => {
          const isDisabled = opt.value === 'urgent' && !priorityEnabled;
          const isSelected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              disabled={isDisabled}
              onClick={() => onChange?.(opt.value)}
              data-testid={`priority-option-${opt.value}`}
              className={`w-full text-left p-3 rounded-xl border-2 transition-all
                ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-sm'}
                ${isSelected ? '' : 'border-gray-200 bg-white hover:border-gray-300'}
              `}
              style={isSelected ? { borderColor: opt.color, background: opt.bg } : undefined}
            >
              <div className="flex items-start gap-3">
                <div className="text-lg">{opt.emoji}</div>
                <div className="flex-1">
                  <div className="font-semibold" style={isSelected ? { color: opt.color } : { color: '#1f2937' }}>{opt.label}</div>
                  <div className="text-xs text-gray-600 mt-0.5">{opt.blurb}</div>
                  {isDisabled && (
                    <div className="text-xs text-gray-500 italic mt-1">Priority Quote is not enabled on your account.</div>
                  )}
                </div>
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5
                    ${isSelected ? '' : 'border-gray-300'}
                  `}
                  style={isSelected ? { borderColor: opt.color, background: opt.color } : undefined}
                >
                  {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 flex gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
        <div>
          <strong>Please use Priority responsibly.</strong> Priority Quotes are designed for urgent requirements where materials are needed within the next 1–3 working days. Repeated use of Priority for non-urgent requests may result in this feature being disabled on your account.
        </div>
      </div>
    </Card>
  );
}

/**
 * Tiny inline badge for use in lists.
 */
export function PriorityBadge({ priority, size = 'sm' }) {
  if (!priority || priority === 'no_rush') return null;
  const opt = OPTIONS.find(o => o.value === priority);
  if (!opt) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-bold uppercase tracking-wider
        ${size === 'lg' ? 'text-xs px-2.5 py-1' : 'text-[10px] px-1.5 py-0.5'}`}
      style={{ background: opt.bg, color: opt.color, border: `1px solid ${opt.border}` }}
      data-testid={`priority-badge-${priority}`}
    >
      {opt.emoji} {priority === 'urgent' ? 'PRIORITY' : 'SOON'}
    </span>
  );
}
